import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { UtensilsCrossed, FolderOpen, PackageOpen, FileText, DollarSign, Wallet } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDashboardStats, useSalesStats } from "@/hooks/useDashboardStats";
import { EdgeFunctionErrorsCard } from "@/components/EdgeFunctionErrorsCard";
import { QueryLoadError } from "@/components/QueryLoadError";
import { deriveLoadState } from "@/lib/queryLoadState";
import { KpiSection } from "@/components/dashboard/KpiSection";
import { useAuth } from "@/contexts/AuthContext";

function formatCurrency(value: number): string {
  return value.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { data: stats } = useDashboardStats();
  const salesQuery = useSalesStats();
  const salesStats = salesQuery.data;
  const salesState = deriveLoadState(salesQuery);
  const { hasRole } = useAuth();
  // quotes RLS admits staff and admin only, so a viewer would see zeros.
  const canSeeKpis = hasRole("staff") || hasRole("admin");

  const cards = [
    { title: "Menu Items", value: stats?.menuItems ?? "—", description: "Active items", icon: UtensilsCrossed, href: "/menu-items" },
    { title: "Categories", value: stats?.categories ?? "—", description: "Item categories", icon: FolderOpen, href: "/categories" },
    { title: "Packages", value: stats?.packages ?? "—", description: "Available packages", icon: PackageOpen, href: "/packages" },
    { title: "Open Quotes", value: stats?.openQuotes ?? "—", description: "Draft & sent", icon: FileText, href: "/quotes" },
  ];

  const maxMonthly = Math.max(...(salesStats?.monthlySales?.map((m) => m.total) ?? [0]), 1);

  return (
    <DashboardLayout title="Dashboard">
      <div className="space-y-8">
        <div>
          <h2 className="font-display text-2xl font-bold text-foreground">Welcome to Tristano's Catering</h2>
          <p className="text-muted-foreground mt-1">Tristano's Catering operations at a glance.</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((stat) => (
            <Card key={stat.title} className="cursor-pointer transition-shadow hover:shadow-md" onClick={() => navigate(stat.href)}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{stat.title}</CardTitle>
                <stat.icon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                <p className="text-xs text-muted-foreground mt-1">{stat.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div>
          <h3 className="font-display text-lg font-semibold text-foreground mb-4">Sales Overview</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Future Amount Sold</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {salesStats ? formatCurrency(salesStats.futureSold) : "—"}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Booked events not yet delivered</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Deposits Collected</CardTitle>
                <Wallet className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {salesStats ? formatCurrency(salesStats.futureDeposits) : "—"}
                </div>
                <p className="text-xs text-muted-foreground mt-1">For future events</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {canSeeKpis && <KpiSection />}

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Monthly Sales (by Event Date)</CardTitle>
            <p className="text-xs text-muted-foreground">Trailing 12 months</p>
          </CardHeader>
          <CardContent>
            {salesStats ? (
              <div className="flex items-end gap-1 h-32">
                {salesStats.monthlySales.map((m) => (
                  <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                    <div
                      className="w-full bg-primary/80 rounded-t transition-all"
                      style={{ height: `${Math.max((m.total / maxMonthly) * 100, 2)}%` }}
                      title={`${m.label}: ${formatCurrency(m.total)}`}
                    />
                    <span className="text-[10px] text-muted-foreground">{m.label}</span>
                  </div>
                ))}
              </div>
            ) : salesState.failed ? (
              <QueryLoadError
                className="h-32 flex flex-col items-center justify-center"
                failedText="Couldn't load sales figures."
                offlineText="You're offline. Sales figures will load when your connection is back."
                state={salesState}
                query={salesQuery}
              />
            ) : (
              <div className="h-32 flex items-center justify-center text-muted-foreground">Loading...</div>
            )}
          </CardContent>
        </Card>

        <EdgeFunctionErrorsCard />
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
